import { useState, useCallback, useEffect } from 'react';
import { uploadAPI } from '../services/api';

// Lee de forma segura un valor JSON guardado en sessionStorage.
const readSession = (key, fallback) => {
  if (!key) return fallback;
  try {
    const raw = sessionStorage.getItem(key);
    return raw !== null ? JSON.parse(raw) : fallback;
  } catch {
    // sessionStorage no disponible o JSON corrupto: usamos el fallback.
    return fallback;
  }
};

// `storageKey` (opcional) activa la persistencia del CSV en sessionStorage,
// para que los datos sobrevivan a un refresh de la página dentro de la sesión.
export const useFileUpload = (showNotification, storageKey) => {
  const dataKey = storageKey ? `${storageKey}:data` : null;
  const colsKey = storageKey ? `${storageKey}:columns` : null;

  const [file, setFile] = useState(null);
  const [data, setData] = useState(() => readSession(dataKey, []));
  const [columns, setColumns] = useState(() => readSession(colsKey, []));
  const [loading, setLoading] = useState(false);

  // Persiste data/columns para que el CSV no se pierda al recargar la página.
  useEffect(() => {
    if (!storageKey) return;
    try {
      sessionStorage.setItem(dataKey, JSON.stringify(data));
      sessionStorage.setItem(colsKey, JSON.stringify(columns));
    } catch {
      // Cuota excedida (CSV muy grande): seguimos solo en memoria.
    }
  }, [data, columns, storageKey, dataKey, colsKey]);

  const handleFileChange = useCallback((selectedFile) => {
    setFile(selectedFile);
  }, []);

  const uploadFile = useCallback(async (fileToUpload, onDataLoaded) => {
    const targetFile = fileToUpload || file;
    if (!targetFile) return null;
    setLoading(true);
    try {
      const res = await uploadAPI.upload(targetFile);
      const uploadedData = res.data.data.filter(r => (parseFloat(r['Cost']) || 0) > 0);
      setData(uploadedData);
      setColumns(res.data.columns);
      onDataLoaded?.(uploadedData);
      return uploadedData;
    } catch (error) {
      const backendMsg = error?.response?.data?.error;
      showNotification?.('error', 'Error al cargar archivo', backendMsg || 'Ocurrio un error al procesar el archivo');
      return null;
    } finally {
      setLoading(false);
    }
  }, [file, showNotification]);

  return {
    file,
    data,
    columns,
    loading,
    handleFileChange,
    uploadFile
  };
};
